import {
  ComponentInCsm,
  CompRef,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { ComponentTypes } from '@wix/editor-const';
import { TPA_COMPONENT_TYPES } from '../constants/componentTypes';
import type { ManifestServiceFlags } from '../ManifestServiceFlags';
import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class ClientSpecMapService {
  static extractCompTypesFromComponent(
    component: ComponentInCsm,
    flags?: ManifestServiceFlags,
  ) {
    const compTypes: string[] = [];

    // Builder component: compType is in data.type
    // @ts-expect-error data.type is not in the types
    if (component.data?.type) {
      // @ts-expect-error data.type is not in the types
      compTypes.push(component.data.type);
    }

    // OOI component: flag off → push both componentId and componentType;
    // flag on → push componentType to avoid duplicates, fall back to componentId
    if (flags?.isLoadManifestWithoutDuplicationsEnabled) {
      const OOIComponentType =
        component.data?.componentType ?? component.componentId;
      if (OOIComponentType) {
        compTypes.push(OOIComponentType);
      }
    } else {
      if (component.componentId) {
        compTypes.push(component.componentId);
      }
      if (component.data?.componentType) {
        compTypes.push(component.data.componentType);
      }
    }

    return compTypes;
  }

  static extractCompTypesFromAppData(appData: EditorClientSpecMapEntry) {
    return [
      ...new Set(
        (appData.components ?? []).flatMap((component) =>
          ClientSpecMapService.extractCompTypesFromComponent(component),
        ),
      ),
    ];
  }

  constructor(
    private dm: DocumentServicesObject,
    private editorPointerAPI: EditorPointerServiceAPI,
  ) {}

  getAppData(compType: string | undefined) {
    if (!compType) {
      return null;
    }

    try {
      const componentData = this.dm.builder.components.getCompAppData(compType);
      if (!componentData) {
        return null;
      }

      const { appDefinitionId } = componentData;
      return this.dm.platform.getAppDataByAppDefId(appDefinitionId) ?? null;
    } catch (error) {
      // console.warn(`Could not check component type '${compType}':`, error);
      return null;
    }
  }

  getAppComponentData(compType: string | undefined) {
    if (!compType) {
      return null;
    }

    const componentData = this.dm.builder.components.getCompAppData(compType);
    if (!componentData) {
      return null;
    }

    const componentId = componentData.componentId;

    const appData = this.getAppData(compType);

    const appDataComponent = appData?.components?.find(
      (component) => component.componentId === componentId,
    );

    if (!appDataComponent) {
      return null;
    }

    return appDataComponent;
  }

  // TODO: handle unique legacy component types!
  // NOTE: type is resolved according to the compRef,
  // innerElementPaths are ignored
  // so it is never resolved for inner ref element
  getCompType(editorPointer: EditorPointer<CompRef>) {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const appData = this.dm.components.data.get(documentPointer);
    const compType = this.dm.components.getType(documentPointer);

    if (
      appData?.widgetId &&
      (compType === ComponentTypes.CustomElement ||
        TPA_COMPONENT_TYPES.includes(compType))
    ) {
      return appData.widgetId;
    }

    return compType;
  }

  getAppDefId(componentId?: string) {
    const installedApps = this.dm.platform.getInstalledApps();
    const pendingApps = this.dm.tpa.getPendingApps();

    return [...installedApps, ...pendingApps].find((app) =>
      app.components?.find(
        (component) => component.componentId === componentId,
      ),
    )?.appDefinitionId;
  }

  isComponentTypesKnown(compTypes: string[]) {
    const installedApps = this.dm.platform.getInstalledApps();
    const pendingApps = this.dm.tpa.getPendingApps();

    const allApps = [...installedApps, ...pendingApps];

    const allKnownTypes = new Set(
      allApps.flatMap(
        // @ts-expect-error app types mismatch with EditorClientSpecMapEntry
        (app) => ClientSpecMapService.extractCompTypesFromAppData(app),
      ),
    );

    return compTypes.every((compType) => allKnownTypes.has(compType));
  }
}
